import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { ProductWarehouseHistoryLinkTab } from 'features/cost-analysis/common-tabs/layerTabs';
import { TenantPage } from 'layouts';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { equipmentService, examService, workstationService } from 'shared/services';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { OptionDescription } from 'shared/types/general';
import { WorkstationForm } from '../components/WorkstationForm';
import { Workstation } from '../types';
import { TabTablePWLayer, TabTableLayer } from 'features/settings/topology/components/tab-layer';

export function EditWorkstation() {
  const { t } = useTranslation();
  const translationPrefix = 'pages.workstation.edit';
  const { workstationId } = useParams();

  const [workstation, setWorkstation] = useState<Workstation>();
  const [equipmentOptions, setEquipmentOptions] = useState<OptionDescription[]>([]);
  const [examOptions, setExamOptions] = useState<OptionDescription[]>([]);
  const [loadingEquipments, setLoadingEquipments] = useState(true);
  const [loadingExams, setLoadingExams] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const isTenantAdminBottom = useIsBottomFromTenantAdmin();
  const isSuperAdminGuard = IsSuperAdminGuard();

  const updateWorkstation = async (values) => {
    dispatch(loadingActions.startLoading());
    workstationService
      .update(workstation.id, values)
      .then(() => {
        enqueueSnackbar('Workstation aggiornata con successo', { variant: 'success' });
        return workstationService.get(workstation.id);
      })
      .then((fetchedWorkstation) => {
        setWorkstation(fetchedWorkstation);
      })
      .finally(() => {
        dispatch(loadingActions.stopLoading());
      });
  };

  useEffect(() => {
    workstationService.get(+workstationId).then(setWorkstation);
    equipmentService.getAllBaseInformation().then((data) => {
      setEquipmentOptions(
        data.map((item) => ({
          ...item,
          label: item.description
        }))
      );
      setLoadingEquipments(false);
    });
    examService.getAllBaseInformation().then((data) => {
      setExamOptions(
        data.map((item) => ({
          ...item,
          label: item.description
        }))
      );
      setLoadingExams(false);
    });
  }, []);

  const submitWorkstationLayer = (
    field: 'product_warehouses' | 'equipments' | 'exams',
    ids: number[]
  ) => {
    if (!workstation?.id) {
      return;
    }

    dispatch(loadingActions.startLoading());
    workstationService
      .update(workstation.id, { [field]: ids })
      .then(() => {
        return workstationService.get(+workstationId).then(setWorkstation);
      })
      .then(() => {
        enqueueSnackbar(t('actions.successfully.updated'), { variant: 'success' });
      })
      .finally(() => {
        dispatch(loadingActions.stopLoading());
      });
  };

  const tabConfig: TabItem[] = workstation
    ? [
        {
          label: t('global.details'),
          component: (
            <WorkstationForm
              workstation={workstation}
              onSubmit={updateWorkstation}
              disableEdit={!isTenantAdminBottom}
            />
          )
        },
        {
          label: t(`global.products`),
          component: (
            <TabTablePWLayer
              title={t('global.products')}
              addElementLabel={t('pages.exams.tabs.products.add_element')}
              selectElementLabel={t('global.product')}
              currentElement={workstation}
              disableEdit={!isTenantAdminBottom}
              onSubmit={(ids) => submitWorkstationLayer('product_warehouses', ids)}
            />
          )
        },
        {
          label: t('global.equipments'),
          component: (
            <TabTableLayer
              title={t('global.equipments')}
              addElementLabel={t('pages.exams.tabs.products.add_element')}
              selectElementLabel={t('global.equipment')}
              selectedElements={workstation?.equipments ?? []}
              options={equipmentOptions}
              optionLabel="label"
              disableEdit={!isTenantAdminBottom}
              isLoading={loadingEquipments}
              onSubmit={(ids) => submitWorkstationLayer('equipments', ids)}
            />
          )
        },
        {
          label: t(`global.exams`),
          component: (
            <TabTableLayer
              title={t('global.exams')}
              addElementLabel={t('pages.exams.tabs.products.add_element')}
              selectElementLabel={t('global.exam')}
              selectedElements={workstation?.exams ?? []}
              options={examOptions}
              optionLabel="label"
              disableEdit={!isTenantAdminBottom}
              isLoading={loadingExams}
              onSubmit={(ids) => submitWorkstationLayer('exams', ids)}
            />
          )
        },
        {
          label: t(`global.history-update`),
          variant: 'admin',
          visible: isSuperAdminGuard,
          component: (
            <ProductWarehouseHistoryLinkTab
              title={t('global.workstation')}
              layer={'workstation'}
              entity_id={workstation.id}
            />
          )
        }
      ]
    : [];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={workstation?.code}>
      <EnhancedTabs tabs={tabConfig} variant="scrollable" />
    </TenantPage>
  );
}

export default EditWorkstation;
